import React, { useEffect, useRef, useState } from 'react';

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function toPixelValue(value) {
    return `${value.toFixed(2)}px`;
}

export function usePrefersReducedMotion() {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

        updatePreference();

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', updatePreference);

            return () => mediaQuery.removeEventListener('change', updatePreference);
        }

        mediaQuery.addListener(updatePreference);
        return () => mediaQuery.removeListener(updatePreference);
    }, []);

    return prefersReducedMotion;
}

export function useRevealInView({ threshold = 0.2, rootMargin = '0px 0px -10% 0px' } = {}) {
    const ref = useRef(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    const [isVisible, setIsVisible] = useState(prefersReducedMotion);

    useEffect(() => {
        if (prefersReducedMotion) {
            setIsVisible(true);
            return undefined;
        }

        const node = ref.current;

        if (!node || typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    setIsVisible(true);
                    observer.disconnect();
                });
            },
            {
                threshold,
                rootMargin,
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [prefersReducedMotion, rootMargin, threshold]);

    return {
        ref,
        isVisible,
        prefersReducedMotion,
    };
}

export function useLandingScrollScene(ref, { reducedMotion = false } = {}) {
    useEffect(() => {
        const node = ref.current;

        if (!node || typeof window === 'undefined' || typeof document === 'undefined') {
            return undefined;
        }

        let animationFrameId = 0;

        const updateScene = () => {
            animationFrameId = 0;

            const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
            const progress = clamp(window.scrollY / maxScroll, 0, 1);

            node.style.setProperty('--landing-scroll-progress', progress.toFixed(4));

            const driftX = reducedMotion ? 0 : -18 + progress * 36;
            const driftY = reducedMotion ? 0 : -12 + progress * 24;

            node.style.setProperty('--landing-drift-x', toPixelValue(driftX));
            node.style.setProperty('--landing-drift-y', toPixelValue(driftY));
        };

        const requestUpdate = () => {
            if (animationFrameId) {
                return;
            }

            animationFrameId = window.requestAnimationFrame(updateScene);
        };

        requestUpdate();

        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);

        return () => {
            window.removeEventListener('scroll', requestUpdate);
            window.removeEventListener('resize', requestUpdate);

            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [ref, reducedMotion]);
}

export function useScrollParallaxVar({ property = '--parallax-y', distance = 18, reducedMotion = false } = {}) {
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;

        if (!node) {
            return undefined;
        }

        const setValue = (value = 0) => {
            node.style.setProperty(property, toPixelValue(value));
        };

        if (reducedMotion || typeof window === 'undefined') {
            setValue(0);
            return undefined;
        }

        let animationFrameId = 0;

        const updateParallax = () => {
            animationFrameId = 0;

            const element = ref.current;

            if (!element) {
                return;
            }

            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight || 1;
            const viewportCenter = viewportHeight / 2;
            const elementCenter = rect.top + rect.height / 2;
            const maxTravel = Math.max(viewportCenter + rect.height / 2, 1);
            const progress = clamp((viewportCenter - elementCenter) / maxTravel, -1, 1);

            setValue(progress * distance);
        };

        const requestUpdate = () => {
            if (animationFrameId) {
                return;
            }

            animationFrameId = window.requestAnimationFrame(updateParallax);
        };

        requestUpdate();

        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);

        return () => {
            window.removeEventListener('scroll', requestUpdate);
            window.removeEventListener('resize', requestUpdate);

            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [distance, property, reducedMotion]);

    return ref;
}

export function useAnimatedNumber(
    target,
    {
        duration = 1000,
        delay = 0,
        decimals = 0,
        start = 0,
        trigger = false,
        reducedMotion = false,
    } = {}
) {
    const [value, setValue] = useState(trigger ? target : start);

    useEffect(() => {
        if (!trigger) {
            setValue(start);
            return undefined;
        }

        if (reducedMotion) {
            setValue(target);
            return undefined;
        }

        let animationFrameId = 0;
        let timeoutId = 0;
        let animationStart = 0;

        const startAnimation = () => {
            const animateFrame = (timestamp) => {
                if (!animationStart) {
                    animationStart = timestamp;
                }

                const progress = Math.min((timestamp - animationStart) / duration, 1);
                const easedProgress = 1 - Math.pow(1 - progress, 3);
                const nextValue = start + (target - start) * easedProgress;

                setValue(nextValue);

                if (progress < 1) {
                    animationFrameId = window.requestAnimationFrame(animateFrame);
                }
            };

            animationFrameId = window.requestAnimationFrame(animateFrame);
        };

        timeoutId = window.setTimeout(startAnimation, delay);

        return () => {
            window.clearTimeout(timeoutId);
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [delay, duration, reducedMotion, start, target, trigger]);

    const precision = Math.max(decimals, 0);
    return Number(value.toFixed(Math.min(precision + 2, 6)));
}

export function AnimatedNumber({
    target,
    duration = 1000,
    delay = 0,
    decimals = 0,
    start = 0,
    trigger = false,
    reducedMotion = false,
    prefix = '',
    suffix = '',
    formatter,
    className = '',
    as: Component = 'span',
}) {
    const value = useAnimatedNumber(target, {
        duration,
        delay,
        decimals,
        start,
        trigger,
        reducedMotion,
    });

    const displayValue = formatter
        ? formatter(value, target)
        : `${prefix}${value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })}${suffix}`;

    return <Component className={className}>{displayValue}</Component>;
}
